import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { useRpcQuery } from "@redotech/redo-web/rpc-query/use-rpc-query";
import { memo } from "react";
import { matchPath, useLocation } from "react-router-dom";

export const MarketingAgentItems = memo(function MarketingAgentItems() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const location = useLocation();

  const isV1Enabled = team.settings.marketing?.marketingAgent?.enabled ?? false;

  const v2FlagLoad = useLoad(
    (signal) =>
      marketingRpcClient.getBooleanFeatureFlag(
        { key: FeatureFlagKey.NEW_MARKETING_AGENT_ONBOARDING },
        { signal },
      ),
    [marketingRpcClient],
  );
  const isV2Enabled = v2FlagLoad.value?.enabled ?? false;

  const isEnabled = isV1Enabled || isV2Enabled;

  const { data: splitInfo, isLoading } = useRpcQuery(
    RedoMarketingRpcClientContext,
    {
      method: "getMarketingAgentSplitInfo",
      args: {},
      options: { enabled: isV1Enabled && !isV2Enabled },
    },
  );

  const hasAnySplit = !isLoading && splitInfo !== null;

  const marketingAgentBaseUrl = routes.marketingAgentHref;
  const anyChildSelected = !!matchPath(
    `${marketingAgentBaseUrl}/*`,
    location.pathname,
  );

  // V2: simple flat item with beta badge
  if (isV2Enabled) {
    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.marketingAgentHref }}
        beta
        FilledIcon={AiStarIcon}
        friendlyName="Marketing Agent"
        id="marketing-agent-setup"
        UnfilledIcon={AiStarIcon}
      />
    );
  }

  return (
    <>
      {/* Simple flat item when test is NOT running - links to setup page */}
      <RedoSecondaryNavItem
        action={{ type: "link", href: routes.marketingAgentHref }}
        FilledIcon={AiStarIcon}
        friendlyName="Marketing Agent"
        hidden={!isEnabled || hasAnySplit}
        id="marketing-agent-setup"
        UnfilledIcon={AiStarIcon}
      />
      {/* Dropdown with children when test IS running */}
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getMarketingAgentSubRoute("dashboard"),
        }}
        anyChildSelected={anyChildSelected}
        FilledIcon={AiStarIcon}
        friendlyName="Marketing Agent"
        hidden={!isEnabled || !hasAnySplit}
        id="marketing-agent"
        UnfilledIcon={AiStarIcon}
      >
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getMarketingAgentSubRoute("dashboard"),
          }}
          friendlyName="Dashboard"
          id="marketing-agent-dashboard"
        />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getMarketingAgentSubRoute("analytics"),
          }}
          friendlyName="Analytics"
          id="marketing-agent-analytics"
        />
        <RedoSecondaryNavItem
          action={{
            type: "link",
            href: routes.getMarketingAgentSubRoute("conversations"),
          }}
          friendlyName="Conversations"
          id="marketing-agent-conversations"
        />
      </RedoSecondaryNavItem>
    </>
  );
});
